import React from 'react';
import ErrorBanner from '@ntuctech/devex-tangram/ErrorBanner';
import SvgAlert from '@ntuctech/devex-tangram/Icon/SvgAlert';
import SvgVerified from '@ntuctech/devex-tangram/Icon/SvgVerified';
import Toast from '@ntuctech/devex-tangram/Toast';
import TooltipDialog from '@ntuctech/devex-tangram/Tooltip/TooltipDialog';
import Typography from '@ntuctech/devex-tangram/Typography';
import { InfoIcon, Text } from '@ntuctech/tangram-web';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { fetcherApiRoute } from '../../services/api/fetcher';
import { FLAGS } from '../../services/feature-flag';
import {
  checkMyinfoStatusApiError,
  newMyinfoPopupTitles,
} from '../../services/myinfo-verification';
import ContentBlock from '../ContentBlock';
import { useFeatures } from '../DynamicFeatureProvider';
import HocProfileManager from '../HocProfileManager';
import SimpleModal from '../SimpleModal';

import {
  MyinfoFullVerifiedDisplay,
  MyinfoNotVerifiedDisplay,
  TooltipHelper,
} from './helperComponents';
import MyInfoMultipleAccModal from './MyInfoMultipleAccModal';

const MyInfoMyInfoDetailsWrapper = styled.div`
  ol {
    margin: 0 auto;
  }

  ul li,
  ol li {
    margin-left: -1rem;
  }

  ul li::marker {
    color: #ffffff;
  }

  ol li::marker {
    font-family: 'Lato', sans-serif;
  }

  .updatedIdList {
    margin-left: 0.5rem;
    margin-top: 1rem;
  }

  .verfiedIdList {
    margin-left: 0.5rem;
    margin-top: 1rem;
    /* margin-bottom: 1rem; */
  }
`;

const HeaderSc = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 0.0625rem solid #eaeaea;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
`;

const HeaderLeftText = styled.div`
  display: flex;
  flex-direction: column;
`;

const LeftTextTopRow = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-left: 0.625rem;
  }
`;

const TypographySubHeaderSc = styled(Typography)`
  color: #999999;
`;

const ErrorBannerSc = styled(ErrorBanner)`
  margin-bottom: 1rem;
`;

const NewMyinfoList = ({ verificationState, promoBanner }) => {
  const renderPromotionBanner = <MyinfoNotVerifiedDisplay promoBanner={promoBanner} />;
  const renderFullVerifiedMsg = <MyinfoFullVerifiedDisplay faqUrl={promoBanner?.faqUrl} />;

  if (verificationState === 'FULLY_VERIFIED') {
    // don't show anything
    return renderFullVerifiedMsg;
  } else {
    return renderPromotionBanner;
  }
};

const ToggleVerifiedLists = ({ newMyinfoVerificationStatus, promoBanner }) => {
  return (
    <NewMyinfoList verificationState={newMyinfoVerificationStatus} promoBanner={promoBanner} />
  );
};

const ExistingAccountUi = ({ email }) => {
  return (
    <>
      <ErrorBannerSc
        label={
          <Typography variant="body3">
            Your Myinfo has already been verified under {<b>{email}</b>}
          </Typography>
        }
        icon={<SvgAlert />}
        attentionLevel="high"
      />
      <Typography variant="body2">To verify under another account:</Typography>
      <ol>
        <li>
          <Typography variant="body2">Log in with {<b>{email}</b>}</Typography>
        </li>
        <li>
          <Typography variant="body2">
            Go to Settings, and select &apos;Delete your Linkpass account&apos;
          </Typography>
        </li>
        <li>
          <Typography variant="body2">Your Myinfo will be verified in this account</Typography>
        </li>
      </ol>
    </>
  );
};

const LeftTextTopIcon = ({ isExistingAcc, newVerifiedStatus, tooltipList }) => {
  return newVerifiedStatus === 'FULLY_VERIFIED' && !isExistingAcc ? (
    <SvgVerified
      size="s"
      color="#2070EF"
      className="verified-icon"
      data-testid="Myinfo-verified-icon"
    />
  ) : (
    <TooltipDialog
      invertedBg
      title="Your data is encrypted and secure."
      content={<TooltipHelper tooltipList={tooltipList} />}
      placement="top"
    >
      <InfoIcon />
    </TooltipDialog>
  );
};

const lastVerifiedText = (myinfoLastVerificationDate, isExistingAcc) => {
  return myinfoLastVerificationDate && !isExistingAcc
    ? `Last verified: ${myinfoLastVerificationDate}`
    : null;
};

const MyInfoDetails = (props) => {
  const { handleOpenForm, isPersonaEnabled } = props;
  const { isFlagEnabled } = useFeatures();
  const isNewMyinfoEnabled = isFlagEnabled(FLAGS.NEW_MYINFO);
  const router = useRouter();
  const showMyinfoSuccessconfirmSuccessModal = router.query?.myinfoConfirmSuccess;
  const showCancelMyinfoUpdateToast = router.query?.cancelMyinfoUpdate;

  const [openUpdate, setOpenUpdate] = React.useState(false);
  const [myinfoRecord, setMyinfoRecord] = React.useState(null);
  const [myinfoError, setMyinfoError] = React.useState('');
  const [myInfoConflict, setMyinfoConflict] = React.useState(null);

  // myinfo conflict ui
  const isExistingAcc = myinfoRecord?.existing_myinfo_data?.uid;
  const existingEmail = myinfoRecord?.existing_myinfo_data?.email;

  React.useEffect(() => {
    if (showCancelMyinfoUpdateToast) {
      setTimeout(() => {
        router.replace('/');
      }, 4000);
    }
    setMyinfoConflict(JSON.parse(localStorage.getItem('myinfo_conflict')));
    fetcherApiRoute(
      isPersonaEnabled ? '/api/v2/myinfo-profile-status' : '/api/myinfo-profile-status',
    )
      .then((res) => {
        setMyinfoRecord(res);
      })
      .catch((err) => {
        setMyinfoError(err.message);
      });
  }, []);

  React.useEffect(() => {
    if (router.query?.vcRedirectUrl) {
      sessionStorage.setItem('vcRedirectUrl', window.location.href.split('vcRedirectUrl=')[1]);
    }
  }, []);

  return (
    isNewMyinfoEnabled && (
      <ContentBlock showShadow={myinfoRecord?.status !== 'FULLY_VERIFIED'}>
        <SimpleModal
          open={openUpdate}
          title="New information added"
          body={
            <ul>
              {newMyinfoPopupTitles.map((identity) => (
                <li key={identity}>{identity}</li>
              ))}
            </ul>
          }
          ctaText="Update"
          ctaAction={handleOpenForm('myinfo')}
          secondaryText="Cancel"
          secondaryAction={() => setOpenUpdate(false)}
          testId="NewMyinfoPrompt"
        />
        <MyInfoMyInfoDetailsWrapper>
          <HeaderSc>
            <HeaderLeftText>
              <LeftTextTopRow>
                <Typography variant="h3" className="ndsHidden-xs-down">
                  Myinfo verification
                </Typography>
                <Typography variant="h5" className="ndsHidden-s-up">
                  Myinfo verification
                </Typography>
                <LeftTextTopIcon
                  isExistingAcc={isExistingAcc}
                  newVerifiedStatus={myinfoRecord?.status}
                  tooltipList={myinfoRecord?.ui?.myinfo_verification?.tooltip_attributes}
                />
              </LeftTextTopRow>
              <TypographySubHeaderSc variant="body4" data-testid="MyinfoVerification">
                {lastVerifiedText(myinfoRecord?.myinfo_last_verified, isExistingAcc)}
              </TypographySubHeaderSc>
            </HeaderLeftText>
          </HeaderSc>
          {!checkMyinfoStatusApiError(myinfoError) && (
            <>
              {isExistingAcc ? (
                <ExistingAccountUi email={existingEmail} />
              ) : (
                <ToggleVerifiedLists
                  newMyinfoVerificationStatus={myinfoRecord?.status}
                  promoBanner={{
                    promoBanner: myinfoRecord?.ui?.myinfo_verification?.promo_banner,
                    ctaText: myinfoRecord?.ui?.myinfo_verification?.cta?.text,
                    description: myinfoRecord?.ui?.myinfo_verification?.description,
                    faqUrl: myinfoRecord?.ui?.faq_url,
                    handleOnClick: handleOpenForm('myinfo'),
                  }}
                />
              )}
            </>
          )}
        </MyInfoMyInfoDetailsWrapper>
        {checkMyinfoStatusApiError(myinfoError) && <Typography>{myinfoError}</Typography>}
        <SimpleModal
          open={showMyinfoSuccessconfirmSuccessModal === 'true'}
          title="Profile updated!"
          body={<Text>Your reward is on the way. We’ll notify you when it’s in your account.</Text>}
          ctaText="Ok, got it"
          ctaAction={() => router.replace('/')}
          testId="MyinfoConfirmSuccessPrompt"
        />
        {myInfoConflict && (
          <MyInfoMultipleAccModal
            verifiedEmail={myInfoConflict?.email}
            errorTitle={myInfoConflict?.errorTitle}
            errorMessage={myInfoConflict?.errorMessage}
            setMyinfoConflict={setMyinfoConflict}
          />
        )}
        {showCancelMyinfoUpdateToast === 'true' && (
          <Toast
            type="alert"
            icon={<SvgAlert data-testid="svg-alert" />}
            message={'Update cancelled'}
            data-testid="cancelUpdateToast"
          />
        )}
      </ContentBlock>
    )
  );
};

export default HocProfileManager(MyInfoDetails);
